import { useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import {
	getMarketingLeadsGetLeadsSummaryQueryKey,
	getMarketingLeadsListLeadsQueryKey,
	useMarketingLeadsUpdateLead,
} from "@/shared/api/generated/marketing-leads/marketing-leads"
import type { Lead, LeadStatus } from "@/shared/api/generated/types/marketing"
import { selectUpdatedLeadData } from "./api-selectors"

export function useLeadDetails(lead: Lead, onClose: () => void) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const [status, setStatus] = useState<LeadStatus>(lead.status)
	const [comment, setComment] = useState(lead.comment)
	const [isDiscardConfirmationOpen, setDiscardConfirmationOpen] =
		useState(false)
	const update = useMarketingLeadsUpdateLead()
	const isDirty = status !== lead.status || comment !== lead.comment

	const requestClose = () => {
		if (isDirty) {
			setDiscardConfirmationOpen(true)
			return
		}

		onClose()
	}

	const keepEditing = () => setDiscardConfirmationOpen(false)
	const discardChanges = () => {
		setDiscardConfirmationOpen(false)
		onClose()
	}

	const save = async () => {
		try {
			selectUpdatedLeadData(
				await update.mutateAsync({
					leadId: lead.id,
					data: { status, comment },
				}),
			)
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: getMarketingLeadsListLeadsQueryKey(),
				}),
				queryClient.invalidateQueries({
					queryKey: getMarketingLeadsGetLeadsSummaryQueryKey(),
				}),
			])
			toast.success(t("leads.notifications.saveSuccess"))
			onClose()
		} catch {
			toast.error(t("leads.notifications.saveError"))
		}
	}

	return {
		status,
		setStatus,
		comment,
		setComment,
		isDirty,
		isDiscardConfirmationOpen,
		isSaving: update.isPending,
		requestClose,
		keepEditing,
		discardChanges,
		save,
	}
}
